import React, { useEffect, useState } from 'react';
import { useApiClient } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';

type Minutes = { soloMinutes: number | null; plannedMinutes: number | null; balanceMinutes: number | null; savingsPercent: number | null };
type Week = Minutes & { weekStart: string; reason: string | null };
type Account = Minutes & { accountId: string; name: string; weeks: Week[]; evaluatedWeeks: number };
type Overview = { publishedWeeks: string[]; accounts: Account[] };

export function ParentTimeAccounts({ groupId, refresh }: { groupId: string; refresh: number }) {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [data, setData] = useState<Overview>();
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string>();
  useEffect(() => {
    let active = true;
    setError('');
    setData(undefined);
    request<Overview>(`/admin/parent-planning/groups/${encodeURIComponent(groupId)}/time-accounts`, { cache: 'no-store' })
      .then(result => { if (active) setData(result); })
      .catch(err => { if (active) setError(err.message || t('time.loadError')); });
    return () => { active = false; };
  }, [groupId, refresh, request, t]);

  const minutes = (value: number | null) => value === null ? '—' : t('time.minutes', { n: value });
  const balance = (value: number | null) => value === null ? '—' : value === 0 ? t('time.even')
    : t(value > 0 ? 'time.saved' : 'time.extra', { n: Math.abs(value) });
  const percent = (value: number | null | undefined) => value == null ? '—' :
    (value / 100).toLocaleString(language === 'de' ? 'de-AT' : 'en-GB', {
      style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
    });
  const date = (day: string) => new Date(day + 'T12:00:00Z').toLocaleDateString(language === 'de' ? 'de-AT' : 'en-GB', { timeZone: 'UTC' });
  const headers = [t('time.parent'), t('time.solo'), t('time.planned'), t('time.balance'), t('time.percent'), t('time.weeks')];
  const weeksWithEntries = new Set(data?.accounts.flatMap(a => a.weeks.map(w => w.weekStart)));
  const emptyWeeks = data?.publishedWeeks.filter(w => !weeksWithEntries.has(w));
  return <section className="admin-panel p-5">
    <div className="admin-section-heading"><div><h2>{t('time.title')}</h2><p>{t('time.hint')}</p></div></div>
    {error ? <p role="alert">{error}</p> : !data ? <p role="status">{t('common.loading')}</p> : <>
      {!data.publishedWeeks.length && <p>{t('time.noWeeks')}</p>}
      {data.accounts.length > 0 && <div className="overflow-x-auto"><table className="w-full text-left text-sm">
        <thead><tr>{headers.map(h => <th key={h} scope="col" className="p-2">{h}</th>)}</tr></thead>
        <tbody>{data.accounts.map(a => <React.Fragment key={a.accountId}>
          <tr className="border-t"><th scope="row" className="p-2">{a.weeks.length
            ? <button className="text-left underline" aria-expanded={expanded === a.accountId}
                onClick={() => setExpanded(expanded === a.accountId ? undefined : a.accountId)}>{a.name}</button> : a.name}</th>
            <td className="p-2">{minutes(a.soloMinutes)}</td><td className="p-2">{minutes(a.plannedMinutes)}</td>
            <td className="p-2">{balance(a.balanceMinutes)}</td><td className="p-2">{percent(a.savingsPercent)}</td><td className="p-2">{t('time.count', { n: a.evaluatedWeeks, all: a.weeks.length })}</td>
          </tr>
          {expanded === a.accountId && <tr><td colSpan={6} className="bg-slate-50 p-3">
            <table className="w-full text-left"><caption className="sr-only">{a.name}</caption>
              <thead><tr>{[t('time.week'), ...headers.slice(1, 5)].map(h => <th key={h} className="p-1" scope="col">{h}</th>)}</tr></thead>
              <tbody>{a.weeks.map(w => <tr key={w.weekStart}><th className="p-1" scope="row">{date(w.weekStart)}</th>
                {w.reason ? <td colSpan={4}>{t('time.unavailable')} · {t('time.reason.' + w.reason)}</td> :
                  <><td>{minutes(w.soloMinutes)}</td><td>{minutes(w.plannedMinutes)}</td><td>{balance(w.balanceMinutes)}</td><td>{percent(w.savingsPercent)}</td></>}
              </tr>)}</tbody>
            </table></td></tr>}
        </React.Fragment>)}</tbody>
      </table></div>}
      {!data.accounts.length && <p>{t('time.noParents')}</p>}
      {!!emptyWeeks?.length && <p>{t('time.emptyWeeks', { dates: emptyWeeks.map(date).join(', ') })}</p>}
    </>}
  </section>;
}
